import moment from "moment";
import refreshTokenService from "./RefreshToken";
import { clearAuthCookies, getTokenExp, setAuthCookies } from "../Scripts/authCookies";

// Refresh this long before the access token expires, so a user returning near the end of its
// lifetime doesn't land on an expired token.
const REFRESH_BEFORE_EXP_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * This function checks the user's localstorage for a jwt token to determine whether the token is still valid
 *
 * @return {{type: sting, status: bool}} key value pair
 */

const handleClearStorage = () => {
  const lastLogin = localStorage.getItem("lastLogin");
  localStorage.clear();
  if (lastLogin !== null) {
    localStorage.setItem("lastLogin", lastLogin);
  }
  clearAuthCookies();
};

function isTokenValid() {
  const token = localStorage.getItem("token");
  const now = new Date().valueOf();

  if (token !== undefined && token !== null && token !== "") {
    const tokenParts = token.split(/\./);
    const tokenDecoded = JSON.parse(window.atob(tokenParts[1]));
    const jwtDate = new Date(tokenDecoded.exp * 1000);
    const newDate = new Date();
    const isExp = newDate > jwtDate;

    if (isExp) {
      handleClearStorage();
      return { type: "normal", status: false };
    }

    if (!tokenDecoded.signed_tc) {
      //console.log("needs to sign tc");
      return { type: "tc", status: true };
    }
    // Valid token
    if (now < jwtDate && tokenDecoded.signed_tc) {
      //console.log("valid token");
      return { type: "normal", status: true };
    }
  } else {
    handleClearStorage();
    return { type: "normal", status: false };
  }
}

/**
 * Whether the stored token is expired or close to it and can be swapped using the refresh token
 * @return {bool}
 */
function shouldRefreshSession() {
  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  if (!token || !refreshToken || refreshToken === "undefined") return false;

  const exp = getTokenExp(token);
  return !exp || exp.getTime() - Date.now() < REFRESH_BEFORE_EXP_MS;
}

let refreshInFlight = null;

/**
 * Swaps the refresh token for a new token pair and updates local storage and the extension cookies.
 * The server rotates refresh tokens, so concurrent callers share one request.
 * @return {Promise<bool>} whether the refresh succeeded
 */
function refreshSession() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = refreshTokenService()
    .then((res) =>
      res.json().then((json) => {
        if (res.status !== 200 || !json.access) return false;

        const { access, refresh } = json;
        const exp = getTokenExp(access) || moment().add(30, "days").toDate();
        localStorage.setItem("token", access);
        if (refresh) {
          localStorage.setItem("refreshToken", refresh);
        }
        localStorage.setItem("tokenExp", moment(exp).format("YYYY-MM-DD HH:mm:ss"));

        try {
          const user = JSON.parse(localStorage.getItem("user"));
          if (user) setAuthCookies(user, access);
        } catch (e) {
          // No user saved; the cookies are written on the next login
        }
        return true;
      })
    )
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * returns a key value pair {pathname: string, message: string, Any}
 *
 * @param {string} resStatus custom message describing server response
 * @param {string} resMessage user displayable message
 * @param {{}} res http response code
 * @return {{pathname: string, message: string, {}}} key value pair
 */
function checkServiceResponse(resJson, res) {
  // console.log("What auth sees");
  //console.log(resJson);
  if (res.status === 401 || res.status === 403) {
    // Forbidden
    handleClearStorage();
    let redirectPath = "/Login";
    return { pathname: redirectPath, message: resJson.message, resStatus: resJson.status, statusInt: res.status };
  }
  // Any other error is not an auth problem, so keep the user logged in
  return {
    pathname: "",
    message: resJson.message,
    resStatus: resJson.status,
    statusInt: res.status,
  };
}
export { checkServiceResponse, isTokenValid, refreshSession, shouldRefreshSession };
